import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SkipSelf,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@angular/forms';
import moment from 'moment-timezone';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Subject } from 'rxjs';
import { finalize, startWith, takeUntil } from 'rxjs/operators';
import { SiteService } from 'src/app/shared/services/site.service';

@Component({
  selector: 'app-date-v2',
  templateUrl: './date-v2.component.html',
  styleUrls: ['./date-v2.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: (container: ControlContainer) => container,
      deps: [[new SkipSelf(), ControlContainer]],
    },
  ],
})
export class DateV2Component implements OnInit, OnDestroy {
  // Inputs
  @Input() controlName!: string;
  @Input() placeholder: string;
  @Input() yearRange: string;
  @Input() autoPopulate: boolean;
  @Input() showCalendar: boolean;
  @Input() showDate: boolean;
  @Input() showTime: boolean;
  @Input() required: boolean;
  @Input() showIcon: boolean;
  @Input() readonly: boolean;

  // Outputs
  @Output() onChange: EventEmitter<Date | null> = new EventEmitter<Date | null>();

  // Dom related
  @ViewChild('overlayPanel') overlayPanelElement!: OverlayPanel;

  externalControl!: FormControl;

  // Internal state
  DATE_FORMAT: string;
  TIME_FORMAT: string;
  DATE_TIME_FORMAT: string;

  minYear?: number;
  maxYear?: number;

  amOrPmOptions: { value: string; name: string }[];
  hourOptions: { value: string; name: string }[];
  monthOptions: { value: string; name: string }[];
  yearOptions: { value: string; name: string }[];

  initialized: boolean;

  internalDate!: Date;
  displayDate: string = '';

  // Subjects
  unsubscribe$ = new Subject();

  constructor(
    private siteService: SiteService,
    private controlContainer: ControlContainer,
    private fb: FormBuilder
  ) {
    this.DATE_FORMAT = 'DD/MM/YYYY';
    this.TIME_FORMAT = 'HH:mm';
    this.DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';

    this.placeholder = 'Select Date / Time';
    this.yearRange = '1900:2030';
    this.autoPopulate = false;
    this.showCalendar = true;
    this.showDate = true;
    this.showTime = true;
    this.required = false;
    this.showIcon = false;
    this.readonly = false;

    this.amOrPmOptions = [
      { value: 'AM', name: 'AM' },
      { value: 'PM', name: 'PM' },
    ];

    // this.hourOptions = [
    //   { value: '1', name: '1' },
    //   { value: '2', name: '2' },
    //   { value: '3', name: '3' },
    //   { value: '4', name: '4' },
    //   { value: '5', name: '5' },
    //   { value: '6', name: '6' },
    //   { value: '7', name: '7' },
    //   { value: '8', name: '8' },
    //   { value: '9', name: '9' },
    //   { value: '10', name: '10' },
    //   { value: '11', name: '11' },
    //   { value: '12', name: '12' },
    // ];

    this.hourOptions = [
      { value: '0', name: '12am - 00' },
      { value: '1', name: '1am - 01' },
      { value: '2', name: '2am - 02' },
      { value: '3', name: '3am - 03' },
      { value: '4', name: '4am - 04' },
      { value: '5', name: '5am - 05' },
      { value: '6', name: '6am - 06' },
      { value: '7', name: '7am - 07' },
      { value: '8', name: '8am - 08' },
      { value: '9', name: '9am - 09' },
      { value: '10', name: '10am - 10' },
      { value: '11', name: '11am - 11' },
      { value: '12', name: '12pm - 12' },
      { value: '13', name: '1pm - 13' },
      { value: '14', name: '2pm - 14' },
      { value: '15', name: '3pm - 15' },
      { value: '16', name: '4pm - 16' },
      { value: '17', name: '5pm - 17' },
      { value: '18', name: '6pm - 18' },
      { value: '19', name: '7pm - 19' },
      { value: '20', name: '8pm - 20' },
      { value: '21', name: '9pm - 21' },
      { value: '22', name: '10pm - 22' },
      { value: '23', name: '11pm - 23' },
    ];

    this.monthOptions = [
      { value: '1', name: 'January' },
      { value: '2', name: 'February' },
      { value: '3', name: 'March' },
      { value: '4', name: 'April' },
      { value: '5', name: 'May' },
      { value: '6', name: 'June' },
      { value: '7', name: 'July' },
      { value: '8', name: 'August' },
      { value: '9', name: 'September' },
      { value: '10', name: 'October' },
      { value: '11', name: 'November' },
      { value: '12', name: 'December' },
    ];

    this.yearOptions = [];

    this.initialized = false;
  }

  ngOnInit(): void {
    // Validate inputs
    if (!(this.showDate || this.showCalendar) && !this.showTime) {
      throw new Error('date-v2: showDate or showTime must be true!');
    }

    // Initialize
    this.populateYearOptions();
    this.initExternalControl();
    this.handleAutoPopulate();

    this.initialized = true;
  }

  onShowTime() {
    this.showTime = !this.showTime;

    // When showTime is `false`, set the time to 0:00
    if (!this.showTime) {
      this.internalDate = moment(this.internalDate).startOf('day').toDate();
      this.displayDate = moment(this.internalDate).format(this.getFormat());
    }
  }

  private initExternalControl() {
    const externalControl = this.controlContainer.control?.get(this.controlName) as FormControl;

    this.externalControl = externalControl;

    this.siteService.addSubscriptionLog(
      this,
      'date-v2.component.ts->initExternalControl->this.externalControl.valueChanges'
    );

    this.externalControl.valueChanges
      .pipe(
        startWith(this.externalControl.value),
        finalize(() =>
          this.siteService.setSubscriptionLogFinalised(
            'date-v2.component.ts->initExternalControl->this.externalControl.valueChanges'
          )
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((date) => {
        if (moment(date).isValid()) {
          this.displayDate = moment(date).format(this.getFormat());
          this.internalDate = date;
        } else {
          this.displayDate = '';
          this.internalDate = moment().startOf('day').toDate();
        }
      });
  }

  private populateYearOptions() {
    const years = this.yearRange.split(':');
    const startYear = +years[0];
    const endYear = +years[1];

    const yearOptions: { name: string; value: string }[] = [];

    for (let year = startYear; year <= endYear; year++) {
      yearOptions.push({
        name: year.toString(),
        value: year.toString(),
      });
    }

    this.yearOptions = yearOptions;
  }

  private handleAutoPopulate() {
    if (this.autoPopulate && !this.externalControl.value) {
      this.externalControl.patchValue(moment().startOf('day').toDate());
    }
  }

  onSelectText(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;
    inputElement.select();
  }

  // Calendar change the date and always set the time to 0:00
  // So we just need to pick the date and keep the time as it is
  onCalendarDateChange(event: Date) {
    this.internalDate = moment(this.internalDate)
      .set('date', event.getDate())
      .set('month', event.getMonth())
      .set('year', event.getFullYear())
      .toDate();
  }

  // onDateChange(unit: string, event: FocusEvent) {
  onDateChange(unit: string, event: any) {
    let value: number | undefined;

    if (unit === 'date') {
      let strValue = (event.target as HTMLInputElement).value;

      // remove `0` from strValue if any
      strValue = strValue.replace(/^0+/, '');

      value = +strValue;
    } else if (unit === 'month') {
      let strValue = event.value;

      value = +strValue - 1;
    } else if (unit === 'year') {
      let strValue = event.value;

      value = +strValue;
    } else if (unit === 'hour') {
      let strValue = event.value;

      value = +strValue;
    } else if (unit === 'minute') {
      let strValue = (event.target as HTMLInputElement).value;

      value = +strValue;
    }

    if (value !== undefined) {
      this.internalDate = moment(this.internalDate)
        .set(unit as any, value)
        .toDate();
    }
  }

  onAmPmChange(amOrPm: any) {
    this.internalDate = moment(this.internalDate)
      .add(amOrPm === 'PM' ? +12 : -12, 'hour')
      .toDate();
  }

  onOk() {
    if (!moment(this.internalDate).isValid()) {
      return;
    }

    // If date only. Always set to start of that day
    if ((this.showDate || this.showCalendar) && !this.showTime) {
      this.internalDate = moment(this.internalDate).startOf('day').toDate();
    }

    this.onChange.emit(this.internalDate);
    this.externalControl.patchValue(this.internalDate);
    this.overlayPanelElement.hide();
  }

  onCancel() {
    this.overlayPanelElement.hide();
  }

  onClear() {
    this.onChange.emit(null);
    this.externalControl.patchValue(null);
    this.overlayPanelElement.hide();
  }

  private getFormat() {
    if (this.showDate && this.showTime) {
      return this.DATE_TIME_FORMAT;
    }

    if (this.showDate) {
      return this.DATE_FORMAT;
    }

    if (this.showTime) {
      return this.TIME_FORMAT;
    }

    return '';
  }

  ngOnDestroy(): void {
    // This component hosts the app's only inline p-calendar, whose [@.disabled]
    // overlay div is stranded in the animation engine's disabledNodes Set on
    // teardown (see SiteService.pruneAnimationDisabledNodes). Sweep one tick
    // after destroy, when the subtree has actually left the document.
    this.siteService.pruneAnimationDisabledNodes();

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
